import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { interpretarCastigo, textoDeNumero } from "@/lib/valorizacion";

interface CampoDeCastigoProps {
  /** El nombre accesible del campo: «Castigo global (%)», «Castigo de Suelos (%)». */
  etiqueta: string;
  /** Cómo se llama el botón de guardar. */
  etiquetaDelBoton: string;
  /** Lo que está guardado; `null` si no hay castigo propio. */
  valor: number | null;
  /** Qué dice el campo vacío («Usa el global (30 %)»). */
  placeholder: string;
  guardando: boolean;
  /** `null` quita el castigo; el servidor valida de nuevo. */
  onGuardar: (valor: number | null) => void;
}

/**
 * Un porcentaje de castigo de liquidación: cualquier valor de 0 a 100.
 *
 * Vacío NO es cero: es «sin castigo propio» y el rubro vuelve al global (o la
 * liquidación queda pendiente si tampoco hay global). El cero es una decisión
 * —liquidar al valor comercial— y se guarda como tal.
 */
export function CampoDeCastigo({ etiqueta, etiquetaDelBoton, valor, placeholder, guardando, onGuardar }: CampoDeCastigoProps) {
  const inicial = valor === null ? "" : textoDeNumero(valor);
  const [texto, setTexto] = useState(inicial);
  const [error, setError] = useState<string | null>(null);
  const cambio = texto.trim() !== inicial;

  function guardar() {
    const lectura = interpretarCastigo(texto);

    if (lectura.estado === "invalido") {
      setError(lectura.mensaje);
      return;
    }

    setError(null);
    onGuardar(lectura.estado === "ok" ? lectura.valor : null);
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <Input
          type="text"
          inputMode="decimal"
          aria-label={etiqueta}
          aria-invalid={error !== null}
          placeholder={placeholder}
          value={texto}
          onChange={(evento) => {
            setTexto(evento.target.value);
            setError(null);
          }}
          onKeyDown={(evento) => {
            if (evento.key === "Enter" && cambio) guardar();
          }}
          className="w-36 font-mono"
        />
        <span aria-hidden className="text-label text-ink-muted font-bold">
          %
        </span>
        <Button type="button" size="sm" variant="outline" aria-label={etiquetaDelBoton} disabled={!cambio || guardando} onClick={guardar}>
          Guardar
        </Button>
      </div>
      {error ? (
        <p role="alert" className="text-caption text-danger-soft-foreground">
          {error}
        </p>
      ) : null}
    </div>
  );
}
